import { useEffect, useState } from "react";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import type { HeroArticleSuggestion } from "@/lib/material";
import {
  isHeroMaterialOption,
  manualMaterialToOption,
  materialRecordToOption,
  timesheetMaterialSource,
} from "@/features/timesheetFlow/lib/timesheetMaterialSource";

export interface MaPositionMaterialValue {
  text: string;
  einheit: string;
  artikel_id: number | null;
  hero_nr: string | null;
  hero_unit: string | null;
  ean: string | null;
}

export interface MaPositionMaterialFieldProps {
  value: MaPositionMaterialValue;
  disabled?: boolean;
  onChange: (patch: Partial<MaPositionMaterialValue>) => void;
  /**
   * Meldet den aktuell getippten (noch nicht per Blur/Enter übernommenen)
   * Freitext nach oben, damit er beim Speichern nicht verloren geht.
   */
  onPendingTextChange?: (text: string) => void;
}

/**
 * Hero-Artikelsuche wie im Stundennachweis-Materialdialog
 * (`TimesheetMaterialDialog` / `LockedAutocomplete`).
 */
export function MaPositionMaterialField({
  value,
  disabled,
  onChange,
  onPendingTextChange,
}: MaPositionMaterialFieldProps) {
  const [materialOption, setMaterialOption] = useState<
    AutocompleteOption<HeroArticleSuggestion> | null
  >(null);

  useEffect(() => {
    setMaterialOption(materialRecordToOption(value));
  }, [
    value.text,
    value.artikel_id,
    value.hero_nr,
    value.hero_unit,
    value.einheit,
    value.ean,
  ]);

  const handleMaterialChange = (opt: AutocompleteOption<HeroArticleSuggestion> | null) => {
    setMaterialOption(opt);
    if (!opt) {
      onChange({
        text: "",
        artikel_id: null,
        hero_nr: null,
        hero_unit: null,
        ean: null,
      });
      return;
    }
    const article = opt.value;
    onChange({
      text: article.label,
      einheit: value.einheit || article.einheit || "",
      artikel_id: article.artikel_id,
      hero_nr: article.hero_nr,
      hero_unit: article.hero_unit ?? article.einheit ?? null,
      ean: article.ean,
    });
  };

  return (
    <div className="[&_.eh-input]:text-sm">
      <LockedAutocomplete<HeroArticleSuggestion>
        placeholder="Material suchen oder frei eingeben …"
        value={materialOption}
        minChars={2}
        allowFreeText
        allowEditLocked={(opt) => !isHeroMaterialOption(opt)}
        createFreeTextOption={manualMaterialToOption}
        loadOptions={timesheetMaterialSource}
        onChange={handleMaterialChange}
        onQueryChange={onPendingTextChange}
        disabled={disabled}
      />
    </div>
  );
}
